(ns frontend.page-components.navbar-components.create-dropdown
  (:require
   [clojure.string :as string]
   [common.entity :as c-entity]
   [common.localization :as loc]
   [common.schema.entities.pipeline :as pipeline]
   [common.schema.entity-creator.utils :as ecu]
   [common.schema.entity-types :as entity-types]
   [common.util :as cu]
   [frontend.db :as app-db]
   [frontend.state.current-session :as session]
   [frontend.view.entity :as entity]
   [reagent.core :as r]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
   [ui.util :as uu]))

(defn dropdown-item
  [entity-type]
  {:value
   [:div
    {:class    ""
     ;; NOTE: used by the robot test framework
     :id       (str "create-button-" (name entity-type))
     :on-click (fn [_]
                 (entity/open-creator! entity-type {}))}
    [:i
     {:class (uu/cm :fal
                    (->> entity-types/entities
                         entity-type
                         :entity/create-icon))}]
    [:span {:class ""}
     (loc/tr :navbar/create-new-entity [(string/lower-case (loc/tr entity-type))])]]
   :custom-item? true})

(defonce opportunity-flow-state (r/atom {}))

(def pipelines-by-entity-type
  {:entity.type/oppo
   session/all-oppo-pipelines
   :entity.type/fundraising-opportunity
   session/all-froppo-pipelines
   :entity.type/sales-opportunity
   session/all-sales-oppo-pipelines
   :entity.type/exit-opportunity
   session/all-exit-oppo-pipelines})

(def stages-by-entity-type
  {:entity.type/oppo
   session/stages
   :entity.type/fundraising-opportunity
   session/opportunity-stages
   :entity.type/sales-opportunity
   session/sales-opportunity-stages
   :entity.type/exit-opportunity
   session/exit-opportunity-stages})

(defn pipelines-where-access
  [entity-type]
  (->> entity-type
       (get pipelines-by-entity-type)
       deref
       (filterv (fn [pipeline]
                  @(r/track session/access-to-pipeline
                            @session/account pipeline)))))

(defn active-pipelines
  [entity-type]
  (->> @(r/track pipelines-where-access entity-type)
       (remove :pipeline/inactive?)
       doall))

(defn all-pipelines
  [entity-type]
  (->> @(r/track pipelines-where-access entity-type)
        ;; active first, inactive then (secondary sorting by lower-case name in session/all-pipelines):
       (sort-by (comp boolean :pipeline/inactive?))
       doall))

(defn has-inactive-pipelines?
  [entity-type]
  (not= (count (active-pipelines entity-type))
        (count (all-pipelines entity-type))))

(defn pipeline-stage-item
  [{:keys [entity-type pipeline stage open-creator-args]}]
  {:key (str "stage-" (:db/id stage))
   :label (c-entity/display-string stage)
   :on-click
   (fn [_]
     (entity/open-creator!
      entity-type
      (merge {:pipeline pipeline
              :ident [:opportunity-creator]
              :skip-success-msg? true
              :stage stage}
             open-creator-args)))})

(defn pipeline-stage-items
  [{:keys [entity-type pipeline] :as args}]
  (let [pipeline-id (:db/id pipeline)
        stage-pipeline-attribute (pipeline/pipeline-stage-key entity-type)
        stages (->> entity-type
                    (get stages-by-entity-type)
                    deref
                    (cu/filter-by stage-pipeline-attribute pipeline-id)
                    (sort-by :common/ord)
                    doall)
        creator-config (:pipeline/creator-config pipeline)
        stage-create-config (ecu/resolve-prevent-stage-config
                             {:config creator-config
                              :stages stages})
        creatable-stages (->> stages
                              (remove (fn [{:keys [db/id]}]
                                        (-> stage-create-config
                                            (get-in [:prevent-create?
                                                     :stages
                                                     id
                                                     :setting])
                                            (= :prevent)))))]
    (cu/concatv
     [{:key            "select-stage-title"
       :section-label? true
       :label          (loc/tr :select-stage)}]
     (doall
      (for [{:keys [db/id] :as stage} creatable-stages]
        ^{:key (str id)}
        (pipeline-stage-item (assoc args :stage stage)))))))

(defn only-inactive-pipelines?
  [pipelines]
  (->> pipelines
       (every? :pipeline/inactive?)))

(defn any-inactive?
  [pipelines]
  (->> pipelines
       (some :pipeline/inactive?)))

(defn pipeline-item
  [{:keys [entity-type pipeline] :as args}]
  (let [pipeline-id (:db/id pipeline)
        creator-config (:pipeline/creator-config pipeline)
        stage-pipeline-attribute (pipeline/pipeline-stage-key entity-type)
        stages (->> entity-type
                    stages-by-entity-type
                    deref
                    (cu/filter-by stage-pipeline-attribute pipeline-id)
                    (sort-by :common/ord)
                    doall)
        stage-create-config (ecu/resolve-prevent-stage-config
                             {:config creator-config
                              :stages stages})
        stages (->> stages
                    (remove (fn [{:keys [db/id]}]
                              (-> stage-create-config
                                  (get-in [:prevent-create?
                                           :stages
                                           id
                                           :setting])
                                  (= :prevent)))))]
    {:key (str "pipeline-" pipeline-id)
     :custom-class "justify-between"
     :label
     [:div
      {:class (uu/cm "flex flex-row items-center gap-3")}
      (c-entity/display-string pipeline)]
     :items
     (cu/concatv
      [{:key            "select-stage-title"
        :section-label? true
        :label          (loc/tr :select-stage)}]
      (doall
       (for [{:keys [db/id] :as stage} stages]
         ^{:key (str id)}
         (pipeline-stage-item (assoc args :stage stage)))))}))

(defn pipeline-items
  [{:keys [entity-type pipelines] :as args}]
  (let [display-all? (or (only-inactive-pipelines? pipelines)
                         (->> @opportunity-flow-state
                              entity-type
                              :show-archived?))
        item-pipelines (cond->> pipelines
                         (not display-all?) (filterv (comp not :pipeline/inactive?)))
        items (->> item-pipelines
                   (mapv (fn [p]
                           (pipeline-item (assoc args :pipeline p)))))
        items (cu/concatv
               [{:key            "select-pipeline-title"
                 :section-label? true
                 :label          (loc/tr :select-pipeline)}]
               items)]
    (cond-> items
      (and (any-inactive? pipelines)
           (not display-all?))
      (conj {:label [:span
                     {:class (uu/cm "text-bold")}
                     (loc/tr :show-all-ellipsis)]
             :on-click (fn [e]
                         (.stopPropagation e)
                         (.preventDefault e)
                         (swap! opportunity-flow-state assoc-in
                                [entity-type
                                 :show-archived?] true))}))))

(defn opportunity-creator-items
  "Builds the dropdown items list for creating an opportunity of `:entity-type`.
   If the user has access to a single pipeline the items are stages directly;
   otherwise pipelines that expand to stages. `:open-creator-args` is merged
   into the final `entity/open-creator!` call, letting callers pass
   `:initial-target`, `:target-locked?`, etc."
  [{:keys [entity-type] :as args}]
  (let [pipelines @(r/track all-pipelines entity-type)]
    (if (cu/one-element? pipelines)
      (pipeline-stage-items (assoc args :pipeline (first pipelines)))
      (pipeline-items (assoc args :pipelines pipelines)))))

(defn opportunity-creation-flow-button
  [entity-type]
  {:label
   [:div
    {:class (uu/cm "flex flex-row items-center gap-3")}
    [:i
     {:class (uu/cm :fal
                    (->> entity-types/entities
                         entity-type
                         :entity/create-icon))}]
    (loc/tr :navbar/create-new-entity
            [(string/lower-case (loc/tr entity-type))])]

   :key (str "new-" entity-type)
   :custom-class "justify-between"
   :items (opportunity-creator-items {:entity-type entity-type})})

(defn legal-entity-item
  [entity-type]
  {:key (str entity-type)
   :label (loc/tr entity-type)
   :on-click
   (fn [_]
     (entity/open-creator!
      :entity.type/investor
      {:legal-entity-type entity-type
       :ident [:investor-creator]
       :skip-success-msg? true}))})

(defn investor-button
  []
  {:label
   [:div
    {:class (uu/cm "flex flex-row items-center gap-3")}
    [:i
     {:class (uu/cm :fal
                    (->> entity-types/entities
                         :entity.type/investor
                         :entity/create-icon))}]
    (loc/tr :navbar/create-new-entity
            [(string/lower-case (loc/tr :investor-profile))])]

   :key "new-investor"
   :custom-class "justify-between"
   :items
   (cu/concatv
    [{:key            "create-investor-legal-entity-type-title"
      :section-label? true
      :label          (loc/tr :select-investor-type)}]
    (->> [:entity.type/person :entity.type/company]
         (mapv legal-entity-item)))})

(defn dropdown
  []
  (let [window-width (:width @app-db/window)]
    [dropdown-menu/view
     {:trigger [dropdown-menu/button-trigger
                (merge {:label  (loc/tr :create)
                        :dom-id "navbar-create-button"}
                       (when (< window-width 576)
                         {:icon [:i.fal.fa-plus]}))]
      :on-open-change (fn [open?]
                        (when-not open?
                          (reset! opportunity-flow-state {})))
      ;;:props {:side "left"}
      :items   (->> (concat
                     (when (and (session/fundraising?)
                                (session/froppo-creation-flow?))
                       [(opportunity-creation-flow-button
                         :entity.type/fundraising-opportunity)])
                     (when (or (session/fundraising?)
                               (session/portfolio?)
                               (session/exit-process-management?))
                       [(investor-button)])
                     [(dropdown-item :entity.type/person)]
                     [(dropdown-item :entity.type/company)]
                     (when (and (session/dealflow?)
                                (session/deal-creation-flow?))
                       [(opportunity-creation-flow-button :entity.type/oppo)])
                     (when (and (session/dealflow?)
                                (not (session/deal-creation-flow?)))
                       [(dropdown-item :entity.type/oppo)])
                     (when (and (session/fundraising?)
                                (not (session/froppo-creation-flow?)))
                       [(dropdown-item :entity.type/fundraising-opportunity)])
                     (when (and (session/exit-process-management?)
                                (session/exit-oppo-creation-flow?))
                       [(opportunity-creation-flow-button :entity.type/exit-opportunity)])
                     (when (and (session/exit-process-management?)
                                (not (session/exit-oppo-creation-flow?)))
                       [(dropdown-item :entity.type/exit-opportunity)])
                     (when (session/portfolio?)
                       [(dropdown-item :entity.type/holding)])
                     (when (or (session/has-funds-access?)
                               (and (session/fundraising?)
                                    (= :plan/fundraising
                                       (->> @session/tenant
                                            :tenant/product-plan
                                            :plan/type))))
                       [(dropdown-item :entity.type/fund-commitment)])
                     (when (session/has-feature-access? :feature/asset-types-real-estate)
                       [(dropdown-item :entity.type/real-estate)])
                     (when (session/has-feature-access? :feature/asset-types-fof)
                       [(dropdown-item :entity.type/fof-asset)])
                     (when (session/has-feature-access? :feature/asset-types-other)
                       [(dropdown-item :entity.type/other-asset)])
                     (when (and (session/sales-pipelines?)
                                (not (session/sales-oppo-creation-flow?)))
                       [(dropdown-item :entity.type/sales-opportunity)])
                     (when (and (session/sales-pipelines?)
                                (session/sales-oppo-creation-flow?))
                       [(opportunity-creation-flow-button :entity.type/sales-opportunity)])
                     (when (session/sales-pipelines?)
                       [(dropdown-item :entity.type/sale-agreement)]))
                    (remove nil?)
                    doall)}]))
